/** Add a step to a custom row's draft (E3, spec 2026-09-18 §6 Edit): id,
 * title, lane, who works it, and where it sits in the reading order. The
 * new step leaves by one `done` exit to whatever follows it; the author
 * rewires from the card. Nothing targets it yet — the validate bar says so. */
import { useState } from 'react'
import {
  Button, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, ModalTitle, NativeSelect,
} from '@/components/ui'
import type { ProcessStepJson, ProcessWorkflowDefinitionJson } from '@/types/workflowDefinition'
import { executorTypesFor, KEY_PATTERN, stepSkeleton, type ExecutorType } from './stepSkeleton'

const SEATED: ExecutorType[] = ['person', 'ai', 'wait']

export function AddStepDialog({
  doc,
  onAdd,
  onClose,
}: {
  doc: ProcessWorkflowDefinitionJson
  onAdd: (stepId: string, step: ProcessStepJson, afterId: string | undefined) => void
  onClose: () => void
}) {
  const stepIds = Object.keys(doc.steps)
  const [id, setId] = useState('')
  const [title, setTitle] = useState('')
  const [lane, setLane] = useState(Object.keys(doc.lanes)[0] ?? '')
  const [type, setType] = useState<ExecutorType>('person')
  const [seat, setSeat] = useState(doc.fallback_seat ?? Object.keys(doc.seats)[0] ?? '')
  const [afterId, setAfterId] = useState(stepIds.at(-1) ?? '')

  const idProblem = !id
    ? null
    : !KEY_PATTERN.test(id)
      ? 'Lowercase letters, digits and underscores, starting with a letter.'
      : id in doc.steps
        ? 'Another step already has this id.'
        : null
  const ready = !!id && !idProblem && title.trim() !== '' && lane !== ''

  function submit() {
    if (!ready) return
    onAdd(
      id,
      stepSkeleton(doc, { title: title.trim(), lane, type, seat: SEATED.includes(type) ? seat : undefined, afterId: afterId || undefined }),
      afterId || undefined,
    )
    onClose()
  }

  return (
    <Modal open onOpenChange={(o) => { if (!o) onClose() }}>
      <ModalContent size="sm" showClose={false}>
        <ModalHeader>
          <ModalTitle>Add a step</ModalTitle>
        </ModalHeader>
        <ModalBody className="space-y-3 text-xs">
          <label className="block space-y-1 font-medium text-foreground">
            <span>Step id</span>
            <Input aria-label="Step id" placeholder="restricted_check" value={id} onChange={(e) => setId(e.target.value)} />
            {idProblem && <span className="block font-normal text-destructive">{idProblem}</span>}
          </label>
          <label className="block space-y-1 font-medium text-foreground">
            <span>Title</span>
            <Input aria-label="Step title" value={title} onChange={(e) => setTitle(e.target.value)} />
          </label>
          <label className="block space-y-1 font-medium text-foreground">
            <span>Lane</span>
            <NativeSelect aria-label="Step lane" value={lane} onChange={(e) => setLane(e.target.value)}>
              {Object.entries(doc.lanes).map(([laneId, l]) => (
                <option key={laneId} value={laneId}>
                  {l.label?.en ?? laneId}
                </option>
              ))}
            </NativeSelect>
          </label>
          <label className="block space-y-1 font-medium text-foreground">
            <span>Who works it</span>
            <NativeSelect aria-label="Step executor" value={type} onChange={(e) => setType(e.target.value as ExecutorType)}>
              {/* An added step is never the entry, so never a conversation. */}
              {executorTypesFor(false).map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </NativeSelect>
          </label>
          {SEATED.includes(type) && (
            <label className="block space-y-1 font-medium text-foreground">
              <span>Seat</span>
              <NativeSelect aria-label="Step seat" value={seat} onChange={(e) => setSeat(e.target.value)}>
                {Object.entries(doc.seats).map(([seatId, s]) => (
                  <option key={seatId} value={seatId}>
                    {s.label?.en ?? seatId}
                  </option>
                ))}
              </NativeSelect>
            </label>
          )}
          <label className="block space-y-1 font-medium text-foreground">
            <span>Place it after</span>
            <NativeSelect aria-label="Place it after" value={afterId} onChange={(e) => setAfterId(e.target.value)}>
              {stepIds.map((stepId, i) => (
                <option key={stepId} value={stepId}>
                  {i + 1} {doc.steps[stepId].title?.en ?? stepId}
                </option>
              ))}
            </NativeSelect>
          </label>
        </ModalBody>
        <ModalFooter>
          <Button size="sm" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button size="sm" disabled={!ready} onClick={submit}>
            Add step
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
